import {
	type CustomerModelDTO,
	type UserModel,
	IntegrationsControllerV3ApiFactory,
	IntegrationUniverseControllerApiFactory,
	InvestmentExportConverterType,
	InvestmentImportConverterType,
	ServiceType,
} from "@eu.mdotm.api/sphere";
import { useApiGen } from "$root/api/hooks";
import { IconWalls, WallOverlay } from "$root/components/IconWall";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { userRepository } from "$root/functional-areas/user/repository";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { zExt } from "$root/third-party-integrations/zod";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { customObjectEntriesFn, customObjectValuesFn, parallelize } from "$root/utils";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	AutoTooltip,
	Banner,
	Collapsible,
	DebouncedSearchInput,
	DefaultCollapsibleContent,
	DefaultCollapsibleHeader,
	DefaultTabTitle,
	Dialog,
	DialogFooter,
	Icon,
	Tab,
	TabGroup,
} from "@mdotm/shadcn/legacy/components";
import {
	Switch,
	Checkbox,
	Radio,
	RadioGroup,
	Button,
	labelClassName,
	SubmitButton,
} from "@mdotm/shadcn/mdotm/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { type MaybeArray, switchExpr } from "@mdotm/shadcn/legacy/utils";
import { Set } from "immutable";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type SpawnUserDialogProps = {
	userData?: MaybeArray<UserModel>;
	customers: CustomerModelDTO[];
	isCurrentUser?: boolean;
};
export function spawnUserDialog(props: SpawnUserDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<UserBox
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async () => {
					resolve();
				}}
			/>
		)),
	);
}

const defaultNewUserData: Partial<UserModel> = {
	email: "",
	name: "",
	surname: "",
	customerId: "",
	enabled: false,
	role: "",
	importFormats: ["SPHERE_TEMPLATE_CONVERTER", "BLOOMBERG_CONVERTER"],
	exportFormats: ["SPHERE_TEMPLATE_CONVERTER", "BLOOMBERG_CONVERTER"],
	importEnabled: undefined,
	importDirectory: undefined,
	services: [
		"ASK_SPHERE",
		"HELP_CENTER",
		"INVESTMENTS",
		"NOTIFICATION_CENTER",
		"REPORTS",
		"SIGNALS",
		"GLOBAL_RISK_MAP",
		"CUSTOM_MARKET_VIEW_COMMENTARY_EDITOR",
		"CUSTOM_MARKET_VIEW_SETTINGS_EDITOR",
		"CUSTOM_QUALITIES",
		"EXPORT",
		"IMPORT",
		"MANDATE_TYPE_SELECTION_VISIBLE",
		"MIXED_PORTFOLIOS",
		"PORTFOLIO_STUDIO_COMMENTARY_TAB",
		"INVESTMENTS_REPORT_TEMPLATE_EDITOR",
		"BULK_ANALYSIS",
	],
};

function getServiceTypeCategory(serviceType: ServiceType) {
	switch (serviceType) {
		case "REPORTS":
		case "INVESTMENTS":
		case "SIGNALS":
		case "CUSTOM_REPORT_CB1":
		case "HELP_CENTER":
		case "NOTIFICATION_CENTER":
		case "ASK_SPHERE":
		case "GLOBAL_RISK_MAP":
			return "Areas";
		case "CUSTOM_QUALITIES":
		case "IMPORT":
		case "EXPORT":
		case "MIXED_PORTFOLIOS":
		case "MANDATE_TYPE_SELECTION_VISIBLE":
		case "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS":
		case "BULK_ANALYSIS":
		case "HIDE_SCENARIO_ANALYSIS_SPHERE_VIEWS":
		case "RISK_MODEL_COLUMN":
		case "GROUPS_UNIVERSE":
		case "NESTED_IN_UNIVERSE":
		case "BYPASS_INSTRUMENT_REVIEW":
		case "SMART_WORKSPACE":
		case "PORTFOLIO_LAB":
		case "PORTFOLIO_IN_QUANTITY":
			return "Portfolio Studio Services";
		case "SHOW_SPHERE_STRATEGICAL_MARKET_VIEWS_ONLY":
		case "CUSTOM_MARKET_VIEW_SETTINGS_EDITOR":
		case "CUSTOM_MARKET_VIEW_COMMENTARY_EDITOR":
			return "Market view";
		case "PORTFOLIO_STUDIO_COMMENTARY_TAB":
		case "COMMENTARY_BUILDER":
		case "STORYFOLIO_V2":
		case "INVESTMENTS_REPORT_TEMPLATE_EDITOR":
			return "Customize Reports";
		default:
			return "Unknow";
	}
}

function getServiceTypeTooltip(serviceType: ServiceType): string | undefined {
	switch (serviceType) {
		case "REPORTS":
			return "Enables the Market signals area (Outlook, Outlook Focus and Macro Indicators).";
		case "INVESTMENTS":
			return "Enables the Portfolio Studio area.";
		case "SIGNALS":
			return "Enables the HMM area.";
		case "CUSTOM_REPORT_CB1":
			return "Enables the Custom Reports area (ex CherryBank Custom Reports).";
		case "HELP_CENTER":
			return "Enables the Help Center area.";
		case "CUSTOM_QUALITIES":
			return "Enable the possibility to set a score to the instruments on the universe, and to set constraints on those values. Currently @deprecated";
		case "IMPORT":
			return "Enable the possibility to import portfolios and universes with custom formats.";
		case "EXPORT":
			return "Enable the possibility to export portfolios with formats.";
		case "NOTIFICATION_CENTER":
			return "Enables the Notification Center area.";
		case "ASK_SPHERE":
			return "Enables the Ask Sphere area.";
		case "SHOW_SPHERE_STRATEGICAL_MARKET_VIEWS_ONLY":
			return "Expose to the user only a limited set of starting scenarios to create the market views. In particular, the ones with 1y+ horizon";
		case "CUSTOM_MARKET_VIEW_SETTINGS_EDITOR":
			return "Users with this service enabled can modify the custom market view template of the organization. The service applies only if the organization have the service 'read market view commentaries' enabled.";
		case "CUSTOM_MARKET_VIEW_COMMENTARY_EDITOR":
			return "Users with this service enabled can modify the commentaries associated to the market views. The service applies only if the organization have the service 'read market view commentaries' enabled.";
		case "MANDATE_TYPE_SELECTION_VISIBLE":
			return "Enable the possibility to select a commentary type (var-based, benchmark-based) for the user portfolios. Used mainly for commentaries generation";
		case "EXPERT_VIEW":
			return "Lorem ipsum dolor sit amet consectetur adipisicing elit. Nemo, incidunt rerum vitae dicta.";
		case "EXPERT":
			return "Lorem ipsum dolor sit amet consectetur adipisicing elit. Nemo, incidunt rerum vitae dicta.";
		case "MIXED_PORTFOLIOS":
			return "Users with this service enabled can create portfolio, by using other portfolio in the composition";
		case "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS":
			return "Users with this service enabled can bypass the min number of instruments required for creating portfolio";
		case "PORTFOLIO_STUDIO_COMMENTARY_TAB":
			return "User with this service can see the commentary in a separate tab inside the portfolio details";
		case "COMMENTARY_BUILDER":
			return "User with this service can access to a new area where he can customize his own commentary templates";
		case "STORYFOLIO_V2":
			return "User with this service can use the second-generation Storyfolio, served by the Storyfolio gateway rather than by the commentary builder";
		case "INVESTMENTS_REPORT_TEMPLATE_EDITOR":
			return "User with this service can create personalized portfolio template that will be shared across the organization";
		case "GLOBAL_RISK_MAP":
			return "Enables Global Risk Map in the HMM area.";
		case "BULK_ANALYSIS":
			return "User with this service can get advance feature to do bulk analysis in portfolio list";
		case "HIDE_SCENARIO_ANALYSIS_SPHERE_VIEWS":
			return "User with this service will have sphere scenario hided in scenario analysis";
		case "RISK_MODEL_COLUMN":
			return "User with this service will have enabled risk model column";
		case "BYPASS_INSTRUMENT_REVIEW":
			return "User with this service can skip review step if the instrument with needed proxy is already filled by sphere";
		case "GROUPS_UNIVERSE":
			return "User with this service can create universe with groups";
		case "NESTED_IN_UNIVERSE":
			return "User with can create universe with portfolio";
		case "PORTFOLIO_LAB":
			return "User with can perform portfolio analysis";
		default:
			return undefined;
	}
}

const UserBox = (
	props: {
		onSubmitAsync(): Promise<void>;
		userData?: MaybeArray<UserModel>;
		customers: CustomerModelDTO[];
		isCurrentUser?: boolean;
	} & MinimumDialogProps,
) => {
	const { show, onAnimationStateChange, onClose, ...rest } = props;
	const { customers, userData, isCurrentUser = false } = rest;
	const mode = userData ? "edit" : "new";
	const isBulkEditing = Array.isArray(userData);
	const { t } = useTranslation();
	const integrationsApi = useApiGen(IntegrationsControllerV3ApiFactory);
	const integrationUniverseApi = useApiGen(IntegrationUniverseControllerApiFactory);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [tabIndex, setTabIndex] = useState(0);

	const { query, filtered, setQuery } = useSearchable({
		collection: customObjectValuesFn(ServiceType).sort(),
		extractFn: (row) => row.replace("_", " "),
	});

	const filteredAndGrouped = useMemo(() => {
		const groups: Record<string, ServiceType[]> = {};
		for (const service of filtered) {
			if (!groups[getServiceTypeCategory(service)]) {
				groups[getServiceTypeCategory(service)] = [];
			}
			groups[getServiceTypeCategory(service)].push(service);
		}
		return groups;
	}, [filtered]);

	const { data: integrationConverter } = useQueryNoRefetch(["queryIntegrationImport"], {
		queryFn: async () => {
			const investmentImportConverter = await axiosExtract(integrationsApi.retrieveInvestmentImportConverterType());
			const investmentExportConverter = await axiosExtract(integrationsApi.retrieveExportConverterType());
			const universeImportConverter = await axiosExtract(integrationUniverseApi.retrieveUniverseImportConverterType());
			return {
				investmentImportConverter,
				investmentExportConverter,
				universeImportConverter,
			};
		},
	});

	const customerSelect = customers.map((el) => {
		return { label: el.name ?? "-", value: el.id! };
	});

	const userOrganizationInfo = useMemo(() => {
		if (Array.isArray(userData)) {
			return undefined;
		}
		return customers.find(({ id }) => userData?.customerId === id);
	}, [customers, userData]);

	const [overrideMethod, setOverrideMethod] = useState<"Add" | "Replace" | "Remove">("Add");

	const userSyncData = useMemo<typeof defaultNewUserData>(
		() => ({
			...defaultNewUserData,
			...(isBulkEditing
				? {
						services: userData.length === 1 ? (userData[0].services ?? []) : [],
						importFormats: userData.length === 1 ? userData[0].importFormats : undefined,
						exportFormats: userData.length === 1 ? userData[0].exportFormats : undefined,
					}
				: userData
					? {
							email: userData.email ?? "",
							name: userData.name ?? "",
							surname: userData.surname ?? "",
							services: userData.services ?? [],
							role: userData.role ?? "",
							enabled: userData.enabled ?? false,
							customerId: userData.customerId ?? "",
							importFormats: userData.importFormats,
							exportFormats: userData.exportFormats,
							importEnabled: userData.importEnabled,
							importDirectory: userData.importDirectory,
						}
					: {}),
		}),
		[isBulkEditing, userData],
	);

	function OverrideServices(user: UserModel, values: typeof userSyncData): UserModel {
		const sanitizedUserServices = user.services ?? [];
		const sanitizedValuesServices = values.services ?? [];
		const sanitizedUserImportFormats = user.importFormats ?? [];
		const sanitizedValuesImportFormats = values.importFormats ?? [];
		const sanitizedUserExportFormats = user.exportFormats ?? [];
		const sanitizedValuesExportFormats = values.exportFormats ?? [];
		return switchExpr(overrideMethod, {
			Add: () =>
				({
					...user,
					services: Set<ServiceType>([...sanitizedUserServices, ...sanitizedValuesServices]).toArray(),
					importFormats: Set<InvestmentImportConverterType>([
						...sanitizedUserImportFormats,
						...sanitizedValuesImportFormats,
					]).toArray(),
					exportFormats: Set<InvestmentExportConverterType>([
						...sanitizedUserExportFormats,
						...sanitizedValuesExportFormats,
					]).toArray(),
				}) satisfies UserModel,
			Remove: () =>
				({
					...user,
					services: sanitizedUserServices.filter((service) => sanitizedValuesServices.includes(service) === false),
					importFormats: sanitizedUserImportFormats.filter(
						(format) => sanitizedValuesImportFormats.includes(format) === false,
					),
					exportFormats: sanitizedUserExportFormats.filter(
						(format) => sanitizedValuesExportFormats.includes(format) === false,
					),
				}) satisfies UserModel,
			Replace: () =>
				({
					...user,
					services: values.services,
					importFormats: values.importFormats ?? user.importFormats,
					exportFormats: values.exportFormats ?? user.exportFormats,
				}) satisfies UserModel,
		});
	}

	function onSubmitAsync() {
		return new Promise((resolve, reject) => {
			handleSubmit(
				async (values) => {
					setIsLoading(true);
					try {
						switch (mode) {
							case "new": {
								await userRepository.createUser(values);
								break;
							}
							case "edit": {
								if (!userData) {
									throw new Error("missing userData in edit mode");
								}
								if (!isBulkEditing) {
									await userRepository.updateUser(userData.id!, values);
								} else {
									const failed: Array<{ err: unknown; user: UserModel }> = [];
									await parallelize(
										userData.map(
											(user) => () =>
												userRepository
													.updateUser(user.id!, OverrideServices(user, values))
													.catch((err) => failed.push({ err, user })),
										),
										{ concurrency: 3 },
									);

									if (failed.length === userData.length) {
										throw failed;
									}

									if (failed.length > 0 && failed.length < userData.length) {
										for (const f of failed) {
											platformToast({
												severity: "error",
												children: `Failed to update user: ${f.user.email}`,
												icon: "Icon-full-error",
											});
										}
									}
								}
								break;
							}
						}
						await props.onSubmitAsync();
					} catch (e) {
						console.error(e);
						platformToast({
							children: t("SETTING_PANEL.CREATE_USER_ERROR"),
							severity: "error",
							icon: "Top-menu-user",
						});
					} finally {
						setIsLoading(false);
					}
				},
				(e) => {
					console.log(e);
					reject(e);
				},
			)().then(resolve, reject);
		});
	}

	const roleOptions = t("SETTING_PANEL.ROLES_OPTIONS", { returnObjects: true });

	const { control, formState, handleSubmit, setValue, watch } = useForm({
		defaultValues: userSyncData ?? defaultNewUserData,
		resolver: zodResolver(
			z.object(
				isBulkEditing
					? {
							services: z.array(zExt.nonEmptyString(t)).min(1, t("REQUIRED_FIELD")),
							importFormats: z.array(z.nativeEnum(InvestmentImportConverterType)).optional(),
							exportFormats: z.array(z.nativeEnum(InvestmentExportConverterType)).optional(),
						}
					: ({
							name: zExt.nonEmptyString(t),
							surname: zExt.nonEmptyString(t),
							email: z
								.string({ required_error: t("REQUIRED_FIELD") })
								.min(1)
								.email(t("login.INVALID_FIELDS_NOT")),
							role: zExt.nonEmptyString(t),
							customerId: zExt.nonEmptyString(t),
							enabled: z.boolean(),
							services: z.array(zExt.nonEmptyString(t)).min(1, t("REQUIRED_FIELD")),
							importFormats: z.array(z.nativeEnum(InvestmentImportConverterType)).optional(),
							exportFormats: z.array(z.nativeEnum(InvestmentExportConverterType)).optional(),
							importEnabled: z.boolean().optional(),
							importDirectory: z.string().optional(),
						} satisfies { [key in keyof UserModel]?: unknown }),
			),
		),
	});

	const serviceTabContent = (
		<>
			{isBulkEditing && (
				<div className="border-b-2 border-solid border-[#616161] pb-2 mb-2 drop-shadow grid space-y-2">
					<span className={labelClassName}>Choose override method</span>
					<Banner severity="warning">
						<p>Be aware, this action is irreversible. Please carefully consider your choices before proceeding.</p>
					</Banner>
					<RadioGroup value={overrideMethod} onChange={(x) => setOverrideMethod(x)} className="mt-2">
						<div className="flex gap-4">
							{["Add", "Remove", "Replace"].map((method) => (
								// `block` belongs on each item rather than on the group: it makes the label an
								// inline-level box, and the line box that creates is this row's height.
								<Radio value={method} key={method} className="block">
									{method}
								</Radio>
							))}
						</div>
					</RadioGroup>
				</div>
			)}

			<FormFields.Custom
				control={control}
				formState={formState}
				label={t("SETTING_PANEL.NAME")}
				name="services"
				className="mb-2"
			>
				{({ field: { onChange, onBlur, value: services }, id }) => (
					<>
						<DebouncedSearchInput
							leftContent={<Icon icon="Search" />}
							externalQuery={query}
							id={id}
							onChange={setQuery}
							className="mb-4"
							placeholder="Search the services you need"
						/>
						<div className="grid gap-2">
							<ForEach collection={customObjectEntriesFn(filteredAndGrouped)} keyProvider={([category]) => category}>
								{([category, categorizedServices]) => (
									<Collapsible header={category} expand={query !== ""}>
										{({ expand }) => (
											<DefaultCollapsibleContent expand={expand}>
												<div className="grid grid-cols-3 gap-2">
													<ForEach collection={categorizedServices}>
														{(service) => (
															<Checkbox
																checked={services?.indexOf(service) !== -1}
																onChange={(checked) => {
																	onChange(
																		checked
																			? [...(services ?? []), service]
																			: (services ?? []).filter((s) => s !== service),
																	);
																	onBlur();
																}}
															>
																{() => {
																	const tooltip = getServiceTypeTooltip(service);
																	if (tooltip) {
																		return (
																			<AutoTooltip
																				position="right"
																				severity="success"
																				trigger={({ innerRef }) => (
																					<span ref={innerRef}>{t(`SERVICES.${service}`)}</span>
																				)}
																			>
																				{tooltip}
																			</AutoTooltip>
																		);
																	}
																	return t(`SERVICES.${service}`);
																}}
															</Checkbox>
														)}
													</ForEach>
												</div>
											</DefaultCollapsibleContent>
										)}
									</Collapsible>
								)}
							</ForEach>
						</div>
					</>
				)}
			</FormFields.Custom>

			<div className="grid gap-4">
				<Collapsible
					header={(params) => (
						<DefaultCollapsibleHeader {...params}>
							<div className="flex space-x-2 items-center">
								<p>{t("SETTING_PANEL.IMPORTS")}</p>
								<FormFields.Custom control={control} formState={formState} label="" name="services">
									{({ field: { onChange, onBlur, value: services } }) => (
										<Switch
											checked={services?.indexOf("IMPORT") !== -1}
											onClick={(e) => e.stopPropagation()}
											onChange={(checked) => {
												onChange(
													checked ? [...(services ?? []), "IMPORT"] : (services ?? []).filter((s) => s !== "IMPORT"),
												);
												onBlur();
											}}
										/>
									)}
								</FormFields.Custom>
							</div>
						</DefaultCollapsibleHeader>
					)}
				>
					{({ expand }) => (
						<DefaultCollapsibleContent expand={expand}>
							<FormFields.Custom control={control} formState={formState} name="importFormats" label="">
								{({ field: { onChange, value: converters } }) => (
									<div className="grid grid-cols-3 gap-2">
										<ForEach
											collection={
												integrationConverter?.investmentImportConverter.filter(
													(converter) => converter !== "UNIPOL_CONVERTER",
												) ?? []
											}
										>
											{(converter) => (
												<Checkbox
													checked={converters?.includes(converter) ?? false}
													disabled={!watch("services")?.includes("IMPORT")}
													className="mr-2"
													onChange={(checked) => {
														onChange(
															checked ? [...(converters ?? []), converter] : converters?.filter((s) => s !== converter),
														);
													}}
												>
													{t(`IMPORT.${converter}`)}
												</Checkbox>
											)}
										</ForEach>
									</div>
								)}
							</FormFields.Custom>
						</DefaultCollapsibleContent>
					)}
				</Collapsible>

				<Collapsible
					header={(params) => (
						<DefaultCollapsibleHeader {...params}>
							<div className="flex space-x-2 items-center">
								<p>{t("SETTING_PANEL.EXPORTS")}</p>
								<FormFields.Custom control={control} formState={formState} label="" name="services">
									{({ field: { onChange, onBlur, value: services } }) => (
										<Switch
											checked={services?.indexOf("EXPORT") !== -1}
											onClick={(e) => e.stopPropagation()}
											onChange={(checked) => {
												onChange(
													checked ? [...(services ?? []), "EXPORT"] : (services ?? []).filter((s) => s !== "EXPORT"),
												);
												onBlur();
											}}
										/>
									)}
								</FormFields.Custom>
							</div>
						</DefaultCollapsibleHeader>
					)}
				>
					{({ expand }) => (
						<DefaultCollapsibleContent expand={expand}>
							<FormFields.Custom control={control} formState={formState} name="exportFormats" label="">
								{({ field: { onChange, value: converters } }) => (
									<div className="grid grid-cols-3 gap-2">
										<ForEach collection={integrationConverter?.investmentExportConverter ?? []}>
											{(converter) => (
												<Checkbox
													checked={converters?.includes(converter) ?? false}
													disabled={!watch("services")?.includes("EXPORT")}
													className="mr-2"
													onChange={(checked) => {
														onChange(
															checked ? [...(converters ?? []), converter] : converters?.filter((s) => s !== converter),
														);
													}}
												>
													{t(`EXPORT.${converter}`)}
												</Checkbox>
											)}
										</ForEach>
									</div>
								)}
							</FormFields.Custom>
						</DefaultCollapsibleContent>
					)}
				</Collapsible>

				{!Array.isArray(userData) && (
					<Collapsible
						header={(params) => (
							<DefaultCollapsibleHeader {...params}>
								<div className="flex space-x-2 items-center">
									<p>Sync</p>
									<FormFields.Custom control={control} formState={formState} label="" name="importEnabled">
										{({ field: { onChange, value: enabled } }) => (
											<Switch
												onClick={(e) => e.stopPropagation()}
												disabled={!userOrganizationInfo?.importData?.enabled}
												checked={enabled ?? false}
												onChange={onChange}
											/>
										)}
									</FormFields.Custom>
								</div>
							</DefaultCollapsibleHeader>
						)}
					>
						{({ expand }) => (
							<DefaultCollapsibleContent expand={expand}>
								<FormFields.Text control={control} formState={formState} label="Directory" name="importDirectory" />
							</DefaultCollapsibleContent>
						)}
					</Collapsible>
				)}
			</div>
		</>
	);

	return (
		<>
			{!userSyncData ? (
				<IconWalls.ErrorData />
			) : (
				<WallOverlay showOverlay={isLoading} overlay={<IconWalls.LoadingData opaque={false} />}>
					<Dialog
						size="xxlarge"
						show={show}
						onClose={onClose}
						onAnimationStateChange={onAnimationStateChange}
						header={
							userData
								? isBulkEditing
									? "Update Users"
									: `Update User ${userData?.name ?? userData?.email ?? ""}`
								: "Create User"
						}
						onSubmitAsync={onSubmitAsync}
						footer={({ state }) => (
							<DialogFooter
								primaryAction={
									<SubmitButton palette="primary" disabled={!formState.isValid}>
										{userData ? t("SETTING_PANEL.UPDATE_USER") : t("SETTING_PANEL.CREATE_USER")}
									</SubmitButton>
								}
								neutralAction={
									<Button palette="tertiary" disabled={state === "loading"} onClick={onClose}>
										{t("CANCEL")}
									</Button>
								}
							/>
						)}
						className={{ "h-[744px]": tabIndex === 1 || isBulkEditing, "[&>div:nth-child(3)]:mt-auto": true }}
					>
						{isBulkEditing ? (
							serviceTabContent
						) : (
							<TabGroup tabIndex={tabIndex} onTabChange={setTabIndex}>
								<Tab
									title={(titleProps) => (
										<DefaultTabTitle
											{...titleProps}
											style={{
												color:
													formState.errors.customerId ||
													formState.errors.email ||
													formState.errors.enabled ||
													formState.errors.name ||
													formState.errors.role ||
													formState.errors.surname
														? themeCSSVars.palette_D500
														: undefined,
											}}
										>
											Info
										</DefaultTabTitle>
									)}
								>
									<div className="grid grid-cols-2 gap-2">
										<FormFields.Text
											control={control}
											formState={formState}
											label={t("SETTING_PANEL.NAME")}
											name="name"
											placeholder={t("SETTING_PANEL.NAME")}
										/>
										<FormFields.Text
											control={control}
											formState={formState}
											label={t("SETTING_PANEL.SURNAME")}
											name="surname"
											placeholder={t("SETTING_PANEL.SURNAME")}
										/>
										<div className="col-span-2">
											<FormFields.Text
												type="email"
												control={control}
												formState={formState}
												label={t("SETTING_PANEL.EMAIL")}
												name="email"
												placeholder={t("SETTING_PANEL.EMAIL")}
												disabled={mode === "edit"}
											/>
										</div>
										<FormFields.Select
											enableSearch
											control={control}
											formState={formState}
											label={t("SETTING_PANEL.CUSTOMER")}
											name="customerId"
											options={customerSelect}
										/>
										<FormFields.Select
											control={control}
											formState={formState}
											label={t("SETTING_PANEL.ROLE")}
											name="role"
											options={roleOptions}
											disabled={mode === "edit"}
										/>
										<FormFields.Checkbox
											control={control}
											formState={formState}
											label={t("SETTING_PANEL.USER_ENABLED")}
											name="enabled"
											disabled={isCurrentUser}
										>
											{t("SETTING_PANEL.USER_ENABLED")}
										</FormFields.Checkbox>
									</div>
								</Tab>
								<Tab
									title={(titleProps) => (
										<DefaultTabTitle
											{...titleProps}
											style={{
												color:
													formState.errors.exportFormats || formState.errors.importFormats || formState.errors.services
														? themeCSSVars.palette_D500
														: undefined,
											}}
										>
											Services
										</DefaultTabTitle>
									)}
								>
									{serviceTabContent}
								</Tab>
							</TabGroup>
						)}
						<div className="flex justify-between items-center">
							<div>
								{tabIndex === 1 &&
									((watch("services") ?? []).length > 0 ? (
										<Button palette="tertiary" size="small" onClick={() => setValue("services", [])}>
											Remove all services
										</Button>
									) : (
										<Button
											palette="tertiary"
											size="small"
											onClick={() => setValue("services", defaultNewUserData.services)}
										>
											Add default services
										</Button>
									))}
							</div>
						</div>
					</Dialog>
				</WallOverlay>
			)}
		</>
	);
};
